import Vue from 'vue';
import {
  fetchAutoNumberPatterns as fetchAutoNumberPatternsApi,
  fetchModelsByMake as fetchModelsByMakeApi,
  findVehicleMake as findVehicleMakeApi,
  getVehicleInfoByNumber as getVehicleInfoByNumberApi,
} from '../api';
import { preloadVehicleMakeList } from '@/features/selectors';
import { Env } from '@/shared/config/environment';

const state = {
  vehicleData: {
    isLoading: false,
  },
  vehicles: {},
  numberPatterns: {
    patterns: null,
    isLoading: false,
    error: null,
  },
  vehicleMakeModels: {},
};

const mutations = {
  SET_LOADING_VEHICLE_DATA(state, isLoading) {
    state.vehicleData.isLoading = isLoading;
  },
  SET_VEHICLE_DATA(state, { number, data }) {
    const vehicles = Array.isArray(data) ? data : [data];
    Vue.set(state.vehicles, number, {
      vehicles,
      cachedAt: Date.now(),
    });
  },
  SET_AUTO_NUMBER_PATTERNS(state, numberPatterns) {
    state.numberPatterns.patterns = numberPatterns;
  },
  SET_LOADING_AUTO_NUMBER_PATTERNS(state, isLoading) {
    state.numberPatterns.isLoading = isLoading;
  },
  SET_AUTO_NUMBER_PATTERNS_ERROR(state, error) {
    state.numberPatterns.error = error;
  },
  SET_VEHICLE_MAKE_MODELS(state, { makeName, models }) {
    Vue.set(state.vehicleMakeModels, makeName, models);
  },
  CLEAR_VEHICLES_CACHE(state) {
    state.vehicles = {};
  },
  CLEAR_VEHICLE_MAKE_MODELS_CACHE(state) {
    state.vehicleMakeModels = {};
  },
};

const actions = {
  async getVehicleInfoByNumber({ commit, dispatch, getters }, number) {
    const vehicleNumber = number.trim().toUpperCase();
    const existing = getters.getVehicleByNumber(vehicleNumber);

    if (existing) {
      const vehicles = existing.vehicles || (Array.isArray(existing) ? existing : [existing]);
      return { vehicles, first: vehicles[0] || null, revisedData: true };
    }

    commit('SET_LOADING_VEHICLE_DATA', true);

    try {
      const response = await getVehicleInfoByNumberApi(vehicleNumber);
      const data = Array.isArray(response) ? response : (response?.data ? [response.data] : []);
      const first = data[0] || null;

      commit('SET_VEHICLE_DATA', {
        number: vehicleNumber,
        data,
      });

      return { vehicles: data, first, revisedData: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося завантажити інформацію про транспортний засіб';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося завантажити інформацію про транспортний засіб',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_VEHICLE_DATA', false);
    }
  },
  async fetchAutoNumberPatterns({ commit, dispatch }) {
    commit('SET_LOADING_AUTO_NUMBER_PATTERNS', true);
    commit('SET_AUTO_NUMBER_PATTERNS', null);
    commit('SET_AUTO_NUMBER_PATTERNS_ERROR', null);

    try {
      const autoNumberPatterns = await fetchAutoNumberPatternsApi();
      commit('SET_AUTO_NUMBER_PATTERNS', autoNumberPatterns);

      return autoNumberPatterns;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося завантажити шаблони номерів транспортних засобів';

      commit('SET_AUTO_NUMBER_PATTERNS_ERROR', errorMessage);
      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося завантажити шаблони номерів транспортних засобів',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_AUTO_NUMBER_PATTERNS', false);
    }
  },
  async findVehicleMake({ commit, dispatch, getters }, make) {
    const cached = getters.getModelsByMakeName(make);

    if (cached !== undefined && cached !== null) {
      return { id: make, name: make };
    }

    try {
      const response = await findVehicleMakeApi(make);
      const responseData = response?.data;
      const mark = responseData?.mark;
      const models = responseData?.model || null;

      if (mark && mark.id && mark.name) {
        commit('SET_VEHICLE_MAKE_MODELS', { makeName: mark.name, models });
        return { id: mark.id, name: mark.name };
      }

      commit('SET_VEHICLE_MAKE_MODELS', { makeName: make, models: null });
      return { id: make, name: make };
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося завантажити список марок транспортних засобів';

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося завантажити список марок транспортних засобів',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    }
  },
  async fetchModelsByMake({ commit, dispatch, getters }, make) {
    const cached = getters.getModelsByMakeName(make);

    if (cached !== undefined && cached !== null) {
      return cached;
    }

    try {
      const models = await fetchModelsByMakeApi(make);
      commit('SET_VEHICLE_MAKE_MODELS', { makeName: make, models });

      return models;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || `Не вдалося завантажити список моделей для марки "${make}"`;

      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: `Не вдалося завантажити список моделей для марки "${make}"`,
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    }
  },
  setVehicleMakeModels({ commit }, { make, models }) {
    commit('SET_VEHICLE_MAKE_MODELS', { makeName: make, models });
  },
  clearVehicleCache({ commit }) {
    commit('CLEAR_VEHICLES_CACHE');
  },
  clearVehicleMakeModelsCache({ commit }) {
    commit('CLEAR_VEHICLE_MAKE_MODELS_CACHE');
  },
};

const getters = {
  isLoadingVehicleData: state => state.vehicleData.isLoading,
  getVehicles: state => state.vehicles,
  getVehicleByNumber: state => number => {
    const entry = state.vehicles[number];
    if (!entry) return null;

    return entry;
  },
  findCachedVehicle: state => (vehicleData) => {
    if (!vehicleData) return null;

    const normalizedNumber = vehicleData.registration_number?.trim()?.toUpperCase();
    const normalizedVin = vehicleData.vin?.trim()?.toUpperCase();
    const cachedByNumber = normalizedNumber ? state.vehicles[normalizedNumber] : null;

    if (cachedByNumber) {
      const vehicles = cachedByNumber.vehicles || (Array.isArray(cachedByNumber) ? cachedByNumber : [cachedByNumber]);
      for (const v of vehicles) {
        const cachedVin = v.vin?.trim()?.toUpperCase();
        if (normalizedVin && cachedVin === normalizedVin) {
          return v;
        }
      }
      return null;
    }

    if (normalizedVin) {
      for (const entry of Object.values(state.vehicles)) {
        const vehicles = entry.vehicles || (Array.isArray(entry) ? entry : [entry]);
        for (const v of vehicles) {
          if (v.vin?.trim()?.toUpperCase() === normalizedVin) {
            return v;
          }
        }
      }
    }

    return null;
  },
  isVehicleDataVerified: state => (vehicleData) => {
    if (!vehicleData) return false;

    const normalizedNumber = vehicleData?.licensePlate || vehicleData?.registration_number?.trim()?.toUpperCase();
    const normalizedVin = vehicleData.vin?.trim()?.toUpperCase();
    const year = vehicleData?.manufactureYear?.toString() || vehicleData.year?.toString();
    const zone = vehicleData?.registrationRegion?.zone ?? vehicleData?.zone ?? vehicleData?.registration_place?.zone;
    const cityId = vehicleData?.registrationRegion?.id || vehicleData?.cityId || vehicleData?.registration_place?.id;
    const vehicleType = vehicleData?.typeId || vehicleData?.type;
    const birthDateOwner = vehicleData?.ownerBirthday?.toString() || vehicleData?.birthDateOwner?.toString();

    const cachedEntry = normalizedNumber ? state.vehicles[normalizedNumber] : null;
    if (!cachedEntry) return false;

    const vehicles = cachedEntry.vehicles || (Array.isArray(cachedEntry) ? cachedEntry : [cachedEntry]);
    const cachedVehicle = normalizedVin
      ? vehicles.find(v => v.vin?.trim()?.toUpperCase() === normalizedVin)
      : vehicles[0];

    if (!cachedVehicle) return false;

    const baseChecks =
      (year && cachedVehicle.year?.toString() === year) &&
      (zone !== undefined && cachedVehicle.city?.zone === zone) &&
      (cityId && cachedVehicle.city?.id === cityId) &&
      (vehicleType && cachedVehicle.type === vehicleType);

    const birthDateCheck = !birthDateOwner ||
      cachedVehicle.birthDateOwner?.toString() === birthDateOwner;

    return baseChecks && birthDateCheck;
  },

  isLoadingAutoNumberPatterns: state => state.numberPatterns.isLoading,
  autoNumberPatternsError: state => state.numberPatterns.error,
  isAutoNumberPatternsReady: state =>
    state.numberPatterns.isLoading === false && state.numberPatterns.patterns !== null,
  getAutoNumberPatterns: state => state.numberPatterns.patterns,

  getPreloadVehicleMakeList: () => preloadVehicleMakeList,
  getVehicleMakeList: state =>
    Object.keys(state.vehicleMakeModels).map(name => ({ id: name, name })),
  getModelsByMakeName: state => makeName => state.vehicleMakeModels[makeName],
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
